/* eslint-disable no-shadow */

import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/languages`;

const state = {
  languages: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.languages != null,
  languages: (state, getters) => (getters.loaded ? state.languages : []),
  noLanguages: (state, getters) => getters.loaded && !getters.languages.length,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.languages = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, { languages }) {
    state.pending = false;
    state.languages = languages;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state, language) {
    state.languages.push(JSON.parse(JSON.stringify(language)));
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state, language) {
    const idx = state.languages.findIndex((p) => p.id === language.id);
    if (idx !== -1) {
      Vue.set(state.languages, idx, JSON.parse(JSON.stringify(language)));
    }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, pid) {
    const idx = state.languages.findIndex((language) => language.id === pid);
    if (idx !== -1) {
      state.languages.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  return err;
}

const actions = {
  async fetchData({ commit }, forced = false) {
    commit('beforeLoading', forced);
    try {
      const result = await ApiService.get(`${endpoint}`);
      const languages = result.data.data;
      commit('data', { languages });
    } catch (error) {
      commit('error', error);
    }
  },
  async createLanguage({ commit }, language) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}`, { ...language });
      commit('created', data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateLanguage({ commit }, language) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${language.id}`, { ...language });
      commit('modified', { ...language });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteLanguage({ commit }, pid) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/${pid}`);
      commit('deleted', pid);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
